// Copyright 2025. WebPros International GmbH. All rights reserved.

import {
    api,
    IApiResponse,
    IPaginateApiResponse,
} from 'common/api/resources/Response';
import {
    IComputeResourceRecoveryReadinessStatusResponse,
    IResilienceStatusTableProps,
    IStartDisasterRecoveryRequest,
} from 'common/api/resources/DisasterRecovery/model';
import { QueryBuilder } from 'common/api/resources/Request/QueryBuilder';
import {
    IDisasterRecoveryDeleteOriginalDataRequest,
    IDisasterRecoveryDownloadOriginalDataRequest,
    IDisasterRecoveryEntitiesRequest,
    IDisasterRecoveryEntityResponse,
    IDisasterRecoveryHistoryRequest,
    IDisasterRecoveryResponse,
} from 'common/api/resources/DisasterRecovery';

export const disasterRecovery = {
    list: (params?: IDisasterRecoveryHistoryRequest) => api.get<IPaginateApiResponse<IDisasterRecoveryResponse[]>>(
        'disaster_recoveries', {
            params: new QueryBuilder(params).build(),
        }),
    create: (params: IStartDisasterRecoveryRequest) => api.post<IApiResponse<IDisasterRecoveryResponse>>('disaster_recoveries', params),
    entities: {
        list: (params: IDisasterRecoveryEntitiesRequest, perPage?: number) => api.get<IPaginateApiResponse<IDisasterRecoveryEntityResponse[]>>('disaster_recovery_entities', {
            params: perPage !== undefined
                ? { ...(new QueryBuilder(params).build()), per_page: perPage }
                : new QueryBuilder(params).build(),
        }),
    },
};

export const disasterRecoveryHistory = {
    deleteOriginalData: (params: IDisasterRecoveryDeleteOriginalDataRequest) =>
        api.post<number[]>('disaster_recoveries/delete_original_data', params),
    downloadOriginalData: (params: IDisasterRecoveryDownloadOriginalDataRequest) =>
        api.post('disaster_recoveries/download_original_data', params)
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([JSON.stringify(response.data, null, 2)]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'disaster_recovery_original_data.json');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                return response;
            }),
};

export const readiness = {
    getCachedReadinessStatus: () => api.get<IResilienceStatusTableProps>(
        'disaster_recoveries/overview/compute_resources'
    ),
    refreshRecoveryReadinessStatus: () =>
        api.post<IApiResponse<{ message: string }>>('disaster_recoveries/overview/compute_resources/refresh'),
    computeResourceReadiness: (id: string) => api.get<IApiResponse<IComputeResourceRecoveryReadinessStatusResponse>>(
        `disaster_recoveries/overview/compute_resources/${id}`
    ),
};